import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ax/Button";
import { PrimaryBar } from "@/components/ax/PrimaryBar";
import { useI18n } from "@/i18n";
import { fetchPlans, rp } from "@/lib/subscription";

export default function LanggananMulai() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [plan, setPlan] = useState("monthly");
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retry, setRetry] = useState(0);
  const annual = plan === "annual";

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchPlans()
      .then((res) => {
        if (alive) setPlans(res?.plans || null);
      })
      .catch(() => { if (alive) setPlans(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [retry]);

  const monthlyPrice = plans?.monthly;
  const annualPrice = plans?.annual;
  const annualPerMonth = plans?.perMonth;
  const annualSaving = plans?.saving;
  const currentPrice = annual ? annualPrice : monthlyPrice;

  return (
    <div data-testid="plan-page">
      <h1 className="type-h1">{t("plan.title")}</h1>
      <p className="mt-3 text-base leading-6 text-ax-text-2" data-testid="plan-price-hint">{t("plan.priceHint")}</p>

      <div role="radiogroup" aria-label={t("plan.seg")} data-testid="price-toggle" className="mt-8 grid grid-cols-2 gap-2">
        {["monthly", "annual"].map((p) => (
          <button
            key={p}
            type="button"
            role="radio"
            aria-checked={plan === p}
            onClick={() => setPlan(p)}
            data-testid={`plan-${p}`}
            className={`min-h-[48px] rounded-[8px] border px-3 text-sm font-medium leading-5 transition-colors duration-150 ${
              plan === p ? "tint-chip-active text-ax-link" : "border-ax-border bg-ax-surface text-ax-text hover:bg-ax-bg"
            }`}
          >
            {t(`plan.${p}`)}
          </button>
        ))}
      </div>

      {loading ? <p role="status" className="mt-6 text-sm text-ax-muted">{t("plan.loading")}</p> :
      !(Number.isFinite(currentPrice) && currentPrice > 0) ? <div className="mt-6"><p role="alert" className="text-sm text-ax-error">{t("plan.pricesUnavailable")}</p><Button variant="ghost" onClick={() => setRetry((n) => n + 1)}>{t("retry")}</Button></div> :
      <div className="mt-6" data-testid="plan-price-block">
        <p className="m-0 font-display text-[32px] font-bold leading-10 tracking-[-0.02em] text-ax-text" data-testid="plan-price">
          {rp(currentPrice)}
          <span className="ml-2 text-base font-normal tracking-normal text-ax-muted">{t(annual ? "plan.perYear" : "plan.perMonth")}</span>
        </p>
        {annual && Number.isFinite(annualPerMonth) && Number.isFinite(annualSaving) && (
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="font-mono text-[13px] leading-[18px] text-ax-text-2" data-testid="plan-equivalent">
              {t("plan.eq", { price: rp(annualPerMonth) })}
            </span>
            <span
              data-testid="plan-saving-badge"
              className="inline-flex items-center rounded-[8px] px-3 py-2 text-sm leading-5 text-ax-text"
              style={{ background: "rgba(201,162,39,0.15)" }}
            >
              {t("plan.save", { price: rp(annualSaving) })}
            </span>
          </div>
        )}
      </div>}

      <section className="mt-8 rounded-[12px] border border-ax-border bg-ax-surface p-4 shadow" aria-label={t("plan.availability")}>
        <p className="caption m-0 text-ax-muted">{t("plan.availability")}</p>
        <p className="m-0 mt-1 text-base leading-6 text-ax-text" data-testid="plan-payment-notice">{t("plan.paymentNotice")}</p>
        <ul className="m-0 mt-3 flex list-none flex-col gap-1 p-0 text-sm leading-5 text-ax-text-2">
          <li>{t("plan.trial")}</li>
          <li>{t("plan.noBilling")}</li>
        </ul>
      </section>

      <PrimaryBar>
        <Button
          variant="primary"
          disabled
          data-testid="plan-go-button"
          className="w-full md:w-auto md:min-w-[200px]"
        >
          {t("plan.paymentUnavailable")}
        </Button>
        <Button variant="ghost" onClick={() => navigate("/akun")}>{t("plan.toAccount")}</Button>
      </PrimaryBar>
    </div>
  );
}
